// Pure quote calculation — extracted verbatim from the Zustand store's calculate()
// action so it can run in BOTH the browser (store.calculate()) and the Cloudflare worker
// (handleListDeals enrichment), letting the review queues show TCV/ARR/margin/assessment
// for every deal without depending on values persisted at save time.
//
// IMPORTANT: keep this a pure module — no React, Zustand, or browser globals — and keep
// the maths byte-identical to the store version. The store now delegates here.
import type { QuoteDetails, SelectedProduct, QuoteCalculations, Rating, OverallRating } from '../types'
import {
  PORTAL_PRICING,
  EXCHANGE_RATES,
  CAC_LOOKUP,
  LTV_MULTIPLIERS,
  ONBOARDING_LOOKUP,
  HOSTING_LOOKUP,
} from '../data'

// Helper: Convert currency
const convertCurrency = (amount: number, from: string, to: string): number => {
  if (from === to) return amount
  return amount * (EXCHANGE_RATES[from]?.[to] ?? 1)
}

// Rating functions - thresholds matched with Calculator Suite
const getTCVRating = (tcv: number): Rating => {
  if (tcv >= 149970) return { rating: 'Great', class: 'metric-great', rank: 4 }
  if (tcv >= 119976) return { rating: 'Good', class: 'metric-good', rank: 3 }
  if (tcv >= 59988) return { rating: 'Average', class: 'metric-average', rank: 2 }
  if (tcv > 0) return { rating: 'Bad', class: 'metric-poor', rank: 1 }
  return { rating: 'Poor', class: 'metric-poor', rank: 0 }
}

const getTCVCommittedRating = (tcvCommitted: number): Rating => {
  if (tcvCommitted >= 149970) return { rating: 'Great', class: 'metric-great', rank: 4 }
  if (tcvCommitted >= 119976) return { rating: 'Good', class: 'metric-good', rank: 3 }
  if (tcvCommitted >= 59988) return { rating: 'Average', class: 'metric-average', rank: 2 }
  if (tcvCommitted > 0) return { rating: 'Bad', class: 'metric-poor', rank: 1 }
  return { rating: 'Poor', class: 'metric-poor', rank: 0 }
}

const getGrossProfitMarginRating = (margin: number): Rating => {
  if (margin >= 70) return { rating: 'Great', class: 'metric-great', rank: 3 }
  if (margin >= 65) return { rating: 'Good', class: 'metric-good', rank: 2 }
  if (margin >= 60) return { rating: 'Average', class: 'metric-average', rank: 1 }
  return { rating: 'Poor', class: 'metric-poor', rank: 0 }
}

const getLTVCACRating = (ratio: number): Rating => {
  if (ratio >= 4.0) return { rating: 'Great', class: 'metric-great', rank: 7 }
  if (ratio >= 3.0) return { rating: 'Good', class: 'metric-good', rank: 5 }
  if (ratio >= 2.0) return { rating: 'Average', class: 'metric-average', rank: 3 }
  if (ratio > 0) return { rating: 'Poor', class: 'metric-poor', rank: 1 }
  return { rating: 'Poor', class: 'metric-poor', rank: 0 }
}

const getPaybackRating = (months: number): Rating => {
  if (months <= 0) return { rating: 'Poor', class: 'metric-poor', rank: 0 }
  if (months <= 9.0) return { rating: 'Great', class: 'metric-great', rank: 4 }
  if (months <= 12.0) return { rating: 'Good', class: 'metric-good', rank: 3 }
  if (months <= 18.0) return { rating: 'Average', class: 'metric-average', rank: 2 }
  return { rating: 'Bad', class: 'metric-poor', rank: 1 }
}

const getOverallAssessmentRating = (totalScore: number): OverallRating => {
  if (totalScore >= 252)
    return {
      rating: 'Great',
      class: 'great',
      message: 'Jacqui says Great, hit the Gong!',
    }
  if (totalScore >= 140)
    return {
      rating: 'Good',
      class: 'good',
      message: 'Jacqui says Good',
    }
  if (totalScore >= 60)
    return {
      rating: 'Average',
      class: 'average',
      message: 'Jacqui says this is Average',
    }
  return {
    rating: 'Poor',
    class: 'poor',
    message: 'Jacqui is speechless at how poor this is, please consult the Finance Team.',
  }
}

/**
 * Compute the full quote metrics from the deal inputs. Returns null when the required
 * fields (plan / currency / term) aren't set yet — matching the store's previous
 * behaviour of leaving calculations null.
 */
export function calculateQuote(
  details: QuoteDetails,
  selectedProducts: SelectedProduct[]
): QuoteCalculations | null {
  const {
    platformFees,
    implementationFee,
    contractTermMonths,
    childAccounts,
    portalSeats,
    supportLevel,
    implementationSupport,
    hostedOneSDK,
    minimumCommitment,
    customerPlan,
    partnerCommission,
    displayCurrency,
  } = details

  // Don't calculate if required fields are not yet filled in
  if (!customerPlan || !displayCurrency || !contractTermMonths) {
    return null
  }

  const childAccountsFee = childAccounts * 2400
  const portalPrice = PORTAL_PRICING[portalSeats]?.price ?? 0

  // Transaction revenue and cost
  const transactionRevenue = selectedProducts.reduce((sum, p) => {
    const convertedPrice = convertCurrency(p.effectivePrice, p.currency, displayCurrency)
    return sum + p.volume * convertedPrice
  }, 0)

  const transactionCost = selectedProducts.reduce((sum, p) => {
    const costToUse = p.customCostPrice !== null ? p.customCostPrice : p.costPrice
    const convertedCost = convertCurrency(costToUse, p.currency, displayCurrency)
    return sum + p.volume * convertedCost
  }, 0)

  // Annual Platform Fees
  const annualPlatformFees =
    platformFees + portalPrice + supportLevel + hostedOneSDK + childAccountsFee

  // ARR (recurring) - includes transaction revenue
  const arr = annualPlatformFees + transactionRevenue

  // MRR
  const mrr = arr / 12

  // Committed ARR (recurring)
  const committedARR = minimumCommitment + annualPlatformFees

  // Total Revenue Year 1
  const transactionOrMinCommit = Math.max(transactionRevenue, minimumCommitment)
  const totalRevenueYr1 =
    annualPlatformFees + implementationFee + implementationSupport + transactionOrMinCommit

  // TCV - Committed
  const platformFeesOverTerm = platformFees * (contractTermMonths / 12)
  const minimumCommitmentOverTerm = minimumCommitment * (contractTermMonths / 12)
  const tcvCommitted =
    platformFeesOverTerm + minimumCommitmentOverTerm + implementationFee + implementationSupport

  // TCV
  const tcv = Math.max(mrr * contractTermMonths + platformFees, tcvCommitted)

  // Gross Profit (full contract term, includes min commit floor + implementation)
  const totalCosts = transactionCost * (contractTermMonths / 12)
  const grossProfit = tcv - totalCosts
  const grossProfitMargin = tcv > 0 ? (grossProfit / tcv) * 100 : 0

  // Min-commit reconciliation. transactionRevenue is the annual usage
  // revenue from volumes × prices. If it's below minimumCommitment the
  // customer effectively pays for unused capacity (and our reported margin
  // is inflated by the commit floor).
  const usageRevenue = transactionRevenue
  const minCommitShortfall = Math.max(0, minimumCommitment - transactionRevenue)

  // Steady-state margin: annual gross margin computed against usage alone,
  // no min-commit floor and no one-off implementation revenue. Pairs with
  // grossProfitMargin (which reflects the year-1 / contract-term picture
  // and CAN be inflated by min commit) so reviewers can see both.
  const steadyStateRevenue = usageRevenue + annualPlatformFees
  const steadyStateMarginPct = steadyStateRevenue > 0
    ? ((steadyStateRevenue - transactionCost) / steadyStateRevenue) * 100
    : 0

  // CAC
  const baseCac = CAC_LOOKUP[customerPlan] ?? -9705
  const partnerCommissionRate = partnerCommission / 100
  const cac = baseCac - (0.11 + partnerCommissionRate) * totalRevenueYr1

  // LTV calculations
  const ltvMultiplier = LTV_MULTIPLIERS[customerPlan] ?? 3
  const ltv = grossProfit * 1.5
  const ltvCacRatio = cac !== 0 ? ltv / Math.abs(cac) : 0

  // LTV/CAC at minimum commit
  const minCommitTotalArr = minimumCommitment + annualPlatformFees
  const minCommitLTV =
    platformFees + minCommitTotalArr * (grossProfitMargin / 100) * ltvMultiplier
  const ltvCacAtMinCommit = cac !== 0 ? minCommitLTV / Math.abs(cac) : 0
  const ltvCacCheck = ltvCacRatio > 1 ? 1 : 0

  // Payback
  const monthlyGrossProfit = grossProfit / contractTermMonths
  const paybackMonths = monthlyGrossProfit > 0 ? Math.abs(cac) / monthlyGrossProfit : 0

  // PC3 Calculation
  const onboarding = ONBOARDING_LOOKUP[customerPlan] ?? ONBOARDING_LOOKUP.Basic
  const hosting = HOSTING_LOOKUP[customerPlan] ?? HOSTING_LOOKUP.Basic
  const pc3 =
    platformFees +
    arr * ltvMultiplier * (grossProfitMargin / 100) +
    cac +
    onboarding.yr1 +
    onboarding.ongoing * (ltvMultiplier - 1) +
    hosting * ltvMultiplier

  // Get ratings
  const tcvRating = getTCVRating(tcv)
  const tcvCommittedRating = getTCVCommittedRating(tcvCommitted)
  const marginRating = getGrossProfitMarginRating(grossProfitMargin)
  const ltvCacRating = getLTVCACRating(ltvCacRatio)
  const ltvCacMinCommitRating = getLTVCACRating(ltvCacAtMinCommit)
  const paybackRating = getPaybackRating(paybackMonths)

  // Overall score
  const overallScore =
    tcvRating.rank *
    tcvCommittedRating.rank *
    marginRating.rank *
    ltvCacRating.rank *
    ltvCacMinCommitRating.rank *
    paybackRating.rank *
    ltvCacCheck

  const overallRating = getOverallAssessmentRating(overallScore)

  return {
    arr,
    mrr,
    committedARR,
    totalRevenueYr1,
    tcv,
    tcvCommitted,
    transactionRevenue,
    transactionCost,
    annualPlatformFees,
    grossProfit,
    grossProfitMargin,
    usageRevenue,
    minCommitShortfall,
    steadyStateMarginPct,
    cac,
    ltv,
    ltvCacRatio,
    ltvCacAtMinCommit,
    paybackMonths,
    pc3,
    tcvRating,
    tcvCommittedRating,
    marginRating,
    ltvCacRating,
    ltvCacMinCommitRating,
    paybackRating,
    overallScore,
    overallRating,
  }
}
